// flag: mover a /models
import { slugify } from '@finect/front-utils/slugify';
import { base64Encode } from '@finect/front-utils/base64';
import { widgetKey } from '@finect/affiliate-product-data/tokens';
import { investmentProducts } from './models/investmentProducts';
import { getArticlePlainText } from './plainText';

export const articleWithSafeHTML = article => ({
  ...article,
  body: {
    ...article.body,
    text: base64Encode(article.body.text)
  }
});

const users = user => ({
  ...user,
  id: user.id,
  type: user.subtype,
  subtype: user.subtype,
  displayName: user.displayName.trim(),
  avatarURL: user.icon ? `/image/${user.icon}` : null,
  icon: user.icon ? user.icon : null,
  username: user.alias,
  alias: user.alias,
  email: user.email,
  firstName: user.name,
  lastName: user.surnames,
  headline: user.headline,
  certification: user.certification,
  adviser: user.adviser,
  stats: user.stats,
  unreadMessages: user.unreadMessages,
  isLoggedIn: true,
  risk: user.risk,
  achievements: user.achievements,
  administrations: user.administrations,
  advised: user.advised,
  aggregations: user.aggregations,
  isProfessional: user.isProfessional,
  organizations: user.organizations,
  features: user.features || [],
  notifications: user.notifications
});

const author = rest => ({
  ...rest,
  // Sin icono, null: la misma regla que `users` un poco mas arriba. Sin la comprobacion, un autor
  // sin foto quedaba con image = "/image/undefined", y eso es una peticion de imagen que devuelve
  // un 404. Quien lo pinta (EntityCard, <Icon>) ya sabe pintar el avatar por defecto cuando no hay
  // imagen; lo que no sabe es distinguir una ruta rota de una buena.
  image: rest.icon ? `/image/${rest.icon}` : null,
  link: `/usuario/${rest.alias}`
});

const comment = (model, authors) => {
  const profile = authors.find(({ id }) => id === model.author);

  return {
    ...model,
    author: profile ? author(profile) : null
  };
};
const comments = (rests, authors) => rests.map(rest => comment(rest, authors)).filter(item => item.author);

const review = (rest, authors) => ({
  id: rest.id,
  author: author(authors.find(el => el.id === rest.author)),
  score: rest.score,
  title: rest.title,
  features: rest.features,
  body: rest.body,
  revision: rest.revision,
  socialStats: rest.socialStats,
  comments: comments(rest.comments, authors)
});
const reviews = (rests, authors) => rests.map(rest => review(rest, authors));

const reviewFeature = rest => ({
  id: rest.id,
  description: rest.description
});
const reviewFeatures = rests => rests.map(rest => reviewFeature(rest));

const answer = (rest, authors) => {
  const answerAuthor = authors.find(({ id }) => id === rest.author);

  return {
    ...rest,
    author: answerAuthor
  };
};

const answers = (rests, authors) => rests.map(rest => answer(rest, authors)).filter(item => item);

const question = (rest, authors) => {
  if (authors.find(el => el.id === rest.author)) {
    return {
      ...rest,
      ...{
        author: author(authors.find(el => el.id === rest.author)),
        link: `/preguntas/${rest.id}/${slugify(rest.title)}`,
        answers: answers(rest.answers, authors)
      }
    };
  }
  return null;
};

const questions = (rests, authors) => rests.map(rest => question(rest, authors)).filter(item => item);

const widget = (rests = [], widgets = []) =>
  rests
    .map((rest) => {
      const widgetModel = widgets?.find(item => widgetKey(item) === rest);

      // If widgetModel is not found, skip processing
      if (!widgetModel) {
        return null;
      }

      // for widgets groups, expand its items with the widget data
      if (rest.includes('GROUP')) {
        return {
          ...widgetModel,
          properties: {
            ...widgetModel?.properties,
            items: widgetModel?.properties?.items.map((item) => {
              const widgetModel2 = widgets.find(({ id }) => id === item.id);
              return {
                ...item,
                ...widgetModel2
              };
            })
          }
        };
      }
      return widgetModel;
    })
    .filter(item => item);

const article = (rest, authors, widgets) => {
  const authorModel = authors.find(el => el && el.hasOwnProperty('id') && el.id === rest.author);
  const editorRaw = rest.revision?.user;
  let editorModel = null;
  if (typeof editorRaw === 'string') {
    editorModel = authors.find(el => el && el.id === editorRaw);
  } else if (editorRaw && typeof editorRaw === 'object') {
    editorModel = editorRaw;
  }
  const widgetsIncluded = rest.widgets?.length > 0 && widgets ? widget(rest.widgets, widgets) : [];

  return {
    ...rest,
    subtitles: rest.subtitles ? typeof rest.subtitles === 'string' ? JSON.parse(rest.subtitles) : rest.subtitles : [],
    metadata: {
      ...rest.metadata || rest.body.metadata
    },
    author: authorModel ? author(authorModel) : null,
    revision: rest.revision ? { ...rest.revision, user: editorModel ? author(editorModel) : null } : rest.revision,
    widgets: widgetsIncluded || []
  };
};

const articles = (rests, authors, widgets) => rests.map(rest => article(rest, authors, widgets));
const safeArticles = (rests, authors) => rests.map(rest => article(articleWithSafeHTML(rest), authors));

const articleComment = (rest, authors) => ({
  ...rest,
  author: author(authors.find(el => el.id === rest.author))
});
const articleComments = (rests, authors) => rests.map(rest => articleComment(rest, authors));

const products = rests =>
  rests.map(rest => ({
    ...rest.entity,
    ...{ subtype: rest.subtype }
  }));

// Cuántos caracteres del cuerpo conserva un vídeo o un podcast de listado.
//
// Ninguna tarjeta pinta el cuerpo: la entradilla sale de `body.metadata.description` y solo cae al
// cuerpo cuando no la hay, y el schema de la home usa la description y la url. Pero el listado
// serializaba el HTML entero de cada pieza en el estado inicial —medido en /podcasts el 2026-10-07:
// 184 KB de los 583 KB del estado eran `body.text`, y en el `homePodcasts` de la home, lo mismo—.
// Se deja un arranque en texto plano que cubre la entradilla de respaldo (160 caracteres) con margen.
// Pasa por `getArticlePlainText`, el mismo limpiador que usa esa entradilla: quita pies de foto,
// marcadores de módulos y entidades, cosas que un simple recorte de etiquetas dejaría dentro.
const MEDIA_TEXT_PREVIEW = 400;

const mediaTextPreview = (text) => {
  if (!text) return text;

  return getArticlePlainText(text).slice(0, MEDIA_TEXT_PREVIEW);
};

const video = (rest, authors) => ({
  ...rest,
  body: rest.body ? { ...rest.body, text: mediaTextPreview(rest.body.text) } : rest.body,
  author: author(authors.find(el => el.id === rest.author))
});

const videos = (rests, authors) => rests.map(rest => video(rest, authors));

export const models = {
  users,
  author,
  review,
  reviews,
  comment,
  comments,
  reviewFeature,
  reviewFeatures,
  question,
  questions,
  investmentProducts,
  article,
  articles,
  safeArticles,
  articleComment,
  articleComments,
  video,
  videos,

  products
};
